import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { temporal } from 'zundo';
import { AlbumInfo, Track } from '../types/album';
import { CornerStyle, DiscDisplayMode, PrintUnitType, TextAlignment, TextDirection, ColumnLayoutType } from '../types/enums';
import {
  BackgroundSettings,
  CornerSettings,
  PRINT_UNIT_DIMENSIONS,
  PrintUnit,
  TextSettings,
  ImageFilter,
  BackTracklistUnit,
  DiscUnit2
} from '../types/printUnit';
import { generateUUID } from '../lib/utils';
import {
  syncAlbumContentOnInfoChange,
  syncAlbumContentOnSelect,
} from '../utils/albumSync';

// 기본 블러 강도 설정
const DEFAULT_BLUR_INTENSITY = 0;

// 기본 이미지 필터 설정
const DEFAULT_IMAGE_FILTER = {
  brightness: 100,
  contrast: 100,
  saturation: 100,
  blur: 0
};

// 기본 모서리 설정
const DEFAULT_CORNER_SETTINGS: CornerSettings = {
  topLeft: { style: CornerStyle.NONE, size: 0 },
  topRight: { style: CornerStyle.NONE, size: 0 },
  bottomLeft: { style: CornerStyle.NONE, size: 0 },
  bottomRight: { style: CornerStyle.NONE, size: 0 }
};

// 기본 배경 설정
const DEFAULT_BACKGROUND_SETTINGS: BackgroundSettings = {
  image: null,
  scale: 1,
  x: 0,
  y: 0,
  color: '#ffffff',
  transparent: false
};

// 기본 텍스트 설정
const DEFAULT_TEXT_SETTINGS: TextSettings = {
  color: '#000000',
  size: 16,
  autoSize: true,
  alignment: TextAlignment.CENTER,
  direction: TextDirection.HORIZONTAL,
  fontFamily: 'Pretendard',
  paddingLeft: 0,
  paddingRight: 0
};

// 초기 인쇄부 설정
const DEFAULT_PRINT_UNITS: Record<PrintUnitType, PrintUnit> = {
  // 전면 커버 (인쇄부 1)
  [PrintUnitType.FRONT]: {
    type: PrintUnitType.FRONT,
    name: '인쇄부 1 (전면 커버)',
    dimension: PRINT_UNIT_DIMENSIONS[PrintUnitType.FRONT],
    background: { ...DEFAULT_BACKGROUND_SETTINGS },
    cornerSettings: { ...DEFAULT_CORNER_SETTINGS },
    showMdLogo: true,
    useBackground: true,
    useText: true, // 텍스트 사용 기본값 true로 변경
    text: { ...DEFAULT_TEXT_SETTINGS }, // 텍스트 설정 추가
    showTitle: true, // 앨범명 표시 기본값
    showArtist: true, // 가수명 표시 기본값
    titlePosition: { x: 100, y: 100 }, // 앨범명 기본 위치 (왼쪽 여백 100px, 위쪽 여백 100px)
    artistPosition: { x: 100, y: 190 }, // 가수명 기본 위치 (앨범명 아래 10px)
    titleFontSize: 80, // 앨범명 기본 글자 크기 (30 → 80)
    artistFontSize: 60, // 가수명 기본 글자 크기 (27 → 60)
    imageFilter: { ...DEFAULT_IMAGE_FILTER }
  },
  
  // 측면 (인쇄부 2)
  [PrintUnitType.SIDE]: {
    type: PrintUnitType.SIDE,
    name: '인쇄부 2 (측면)',
    dimension: PRINT_UNIT_DIMENSIONS[PrintUnitType.SIDE],
    background: { ...DEFAULT_BACKGROUND_SETTINGS },
    cornerSettings: { ...DEFAULT_CORNER_SETTINGS },
    showMdLogo: false,
    text: { ...DEFAULT_TEXT_SETTINGS },
    content: '',
    useBackground: true,
    useText: true,
    blurIntensity: DEFAULT_BLUR_INTENSITY,
    imageFilter: { ...DEFAULT_IMAGE_FILTER }
  },

  // 뒷면 상단 (인쇄부 3)
  [PrintUnitType.BACK_TOP]: {
    type: PrintUnitType.BACK_TOP,
    name: '인쇄부 3 (뒷면 상단)',
    dimension: PRINT_UNIT_DIMENSIONS[PrintUnitType.BACK_TOP],
    background: { ...DEFAULT_BACKGROUND_SETTINGS },
    cornerSettings: { ...DEFAULT_CORNER_SETTINGS },
    showMdLogo: false,
    text: { ...DEFAULT_TEXT_SETTINGS },
    content: '',
    useTwoLines: false,
    useBackground: true,
    useText: true,
    blurIntensity: DEFAULT_BLUR_INTENSITY,
    imageFilter: { ...DEFAULT_IMAGE_FILTER }
  },
  
  // 뒷면 (인쇄부 4)
  [PrintUnitType.BACK]: {
    type: PrintUnitType.BACK,
    name: '인쇄부 4 (뒷면 곡 리스트)',
    dimension: PRINT_UNIT_DIMENSIONS[PrintUnitType.BACK],
    background: { ...DEFAULT_BACKGROUND_SETTINGS },
    cornerSettings: { ...DEFAULT_CORNER_SETTINGS },
    showMdLogo: true,
    text: { ...DEFAULT_TEXT_SETTINGS, size: 61, autoSize: false },
    useBackground: true,
    useText: true,
    columnLayout: ColumnLayoutType.TWO_COLUMN_EQUAL,
    useTwoColumns: true,
    lineHeight: 1.2,
    blurIntensity: DEFAULT_BLUR_INTENSITY,
    // 흰색 투명도 레이어 설정
    showWhiteOverlay: false, // 기본값: 비활성화
    whiteOverlayOpacity: 0.5, // 기본값: 50% 투명도
    imageFilter: { ...DEFAULT_IMAGE_FILTER }
  },
  
  // 디스크 본체 2 (인쇄부 5)
  [PrintUnitType.DISC2]: {
    type: PrintUnitType.DISC2,
    name: '인쇄부 5 (본체(앨범자켓만 표시))',
    dimension: PRINT_UNIT_DIMENSIONS[PrintUnitType.DISC2],
    background: { ...DEFAULT_BACKGROUND_SETTINGS },
    cornerSettings: { ...DEFAULT_CORNER_SETTINGS },
    showMdLogo: true,
    text: { ...DEFAULT_TEXT_SETTINGS },
    displayMode: DiscDisplayMode.ALBUM_COVER_ONLY,
    useBackground: true,
    useText: true,
    showTitle: true,
    showArtist: true,
    titlePosition: { x: 50, y: 50 }, // 왼쪽 여백 50px, 위쪽 여백 50px
    artistPosition: { x: 50, y: 140 }, // 앨범명 아래 90px 간격
    titleFontSize: 80, // 앨범명 기본 글자 크기
    artistFontSize: 60, // 가수명 기본 글자 크기
    imageFilter: { ...DEFAULT_IMAGE_FILTER }
  },
  
  // 디스크 측면 (인쇄부 6)
  [PrintUnitType.DISC_SIDE]: {
    type: PrintUnitType.DISC_SIDE,
    name: '인쇄부 6 (디스크 측면)',
    dimension: PRINT_UNIT_DIMENSIONS[PrintUnitType.DISC_SIDE],
    background: { ...DEFAULT_BACKGROUND_SETTINGS },
    cornerSettings: { ...DEFAULT_CORNER_SETTINGS },
    showMdLogo: false,
    text: { ...DEFAULT_TEXT_SETTINGS },
    content: '',
    useBackground: true,
    useText: true,
    blurIntensity: DEFAULT_BLUR_INTENSITY,
    imageFilter: { ...DEFAULT_IMAGE_FILTER }
  }
};

// 스토어 상태 인터페이스
interface StoreState {
  // 앨범 정보
  albumInfo: AlbumInfo;
  setAlbumInfo: (info: Partial<AlbumInfo>) => void;

  // 곡 리스트
  tracklist: Track[];
  addTrack: (title: string) => void;
  updateTrack: (id: string, title: string) => void;
  removeTrack: (id: string) => void;
  setTracklist: (tracks: Track[]) => void;
  reorderTrack: (startIndex: number, endIndex: number) => void;

  // 인쇄부 관련
  printUnits: Record<PrintUnitType, PrintUnit>;
  selectedPrintUnit: PrintUnitType | null;
  setSelectedPrintUnit: (type: PrintUnitType) => void;
  updatePrintUnit: <T extends PrintUnitType>(type: T, updates: Partial<PrintUnit>) => void;

  // 인쇄부 옵션 패널 활성 탭 (이미지/배경색/모서리/로고/텍스트/필터)
  activePrintUnitOptionTab: string;
  setActivePrintUnitOptionTab: (tab: string) => void;

  // 클립보드 (스타일 복사/붙여넣기, persist 제외)
  clipboard: PrintUnit | null;
  setClipboard: (unit: PrintUnit) => void;
  applyClipboard: (targetType: PrintUnitType) => void;

  // 레이아웃 관련
  resetAll: () => void;
  // 새 앨범 만들기용 초기화 함수 (페이지 리로드 없음)
  resetForNewAlbum: () => void;
  // 로컬 스토리지 리셋 함수
  resetStorage: () => void;
}

// 스타일 복사 시 유지할 safe 필드 목록 (content/텍스트 내용 제외)
const STYLE_SAFE_KEYS: (keyof PrintUnit)[] = ['background', 'cornerSettings', 'imageFilter'];

// Zustand 스토어 생성
export const useStore = create<StoreState>()(
  temporal(
  persist(
    (set, get) => ({
      // 앨범 정보 초기값
      albumInfo: {
        title: '',
        artist: '',
        coverImage: null,
        tags: []
      },
      
      // 앨범 정보 수정 함수
      setAlbumInfo: (info) => {
        return set((state) => {
          const newAlbumInfo = { ...state.albumInfo, ...info };

          // 앨범 정보가 변경되면 SIDE/BACK_TOP/DISC_SIDE content 자동 업데이트
          // (제목/가수 중 하나라도 있을 때만 덮어쓰기, 둘 다 비면 기존 값 유지)
          const updatedPrintUnits = syncAlbumContentOnInfoChange(
            state.printUnits,
            newAlbumInfo,
          );

          return {
            albumInfo: newAlbumInfo,
            printUnits: updatedPrintUnits
          };
        });
      },
      
      // 곡 리스트 초기값
      tracklist: [],
      
      // 곡 추가 함수
      addTrack: (title) => 
        set((state) => ({ 
          tracklist: [...state.tracklist, { id: generateUUID(), title }] 
        })),
      
      // 곡 수정 함수
      updateTrack: (id, title) => 
        set((state) => ({ 
          tracklist: state.tracklist.map(track => 
            track.id === id ? { ...track, title } : track
          ) 
        })),
      
      // 곡 삭제 함수
      removeTrack: (id) => 
        set((state) => ({ 
          tracklist: state.tracklist.filter(track => track.id !== id) 
        })),
      
      // 곡 리스트 전체 설정 함수
      setTracklist: (tracks) => 
        set(() => ({ tracklist: tracks })),
      
      // 곡 순서 변경 함수
      reorderTrack: (startIndex, endIndex) => 
        set((state) => {
          const result = Array.from(state.tracklist);
          const [removed] = result.splice(startIndex, 1);
          result.splice(endIndex, 0, removed);
          
          return { tracklist: result };
        }),
      
      // 인쇄부 초기값
      printUnits: DEFAULT_PRINT_UNITS,
      
      // 선택된 인쇄부 초기값
      selectedPrintUnit: PrintUnitType.FRONT,
      
      // 인쇄부 선택 함수
      setSelectedPrintUnit: (type) =>
        set((state) => {
          // 선택된 인쇄부가 SIDE/BACK_TOP/DISC_SIDE이고 기존 content가 비어있을 때만
          // 앨범 정보로 자동 채우기 (기존 커스텀 content는 보존)
          const updatedPrintUnits = syncAlbumContentOnSelect(
            state.printUnits,
            type,
            state.albumInfo,
          );

          return {
            selectedPrintUnit: type,
            printUnits: updatedPrintUnits
          };
        }),
      
      // 인쇄부 업데이트 함수
      updatePrintUnit: (type, updates) => {
        set((state) => {
          const newPrintUnit = { ...state.printUnits[type], ...updates };
          return {
            printUnits: {
              ...state.printUnits,
              [type]: newPrintUnit
            }
          };
        });
      },

      // 인쇄부 옵션 패널 활성 탭 초기값
      activePrintUnitOptionTab: 'image',
      setActivePrintUnitOptionTab: (tab) => set({ activePrintUnitOptionTab: tab }),

      // 클립보드 초기값 (persist 제외)
      clipboard: null,

      // 현재 인쇄부의 스타일을 클립보드에 복사
      setClipboard: (unit: PrintUnit) => set({ clipboard: structuredClone(unit) }),

      // 클립보드의 스타일을 대상 인쇄부에 적용 (background, cornerSettings, imageFilter만)
      applyClipboard: (targetType: PrintUnitType) => {
        const cb = get().clipboard;
        if (!cb) return;
        const target = get().printUnits[targetType];
        if (!target) return;
        const merged: PrintUnit = { ...target };
        for (const key of STYLE_SAFE_KEYS) {
          if (key in cb) {
            (merged as Record<string, unknown>)[key] = structuredClone((cb as Record<string, unknown>)[key]);
          }
        }
        get().updatePrintUnit(targetType, merged);
      },

      // 새 앨범 만들기용 초기화 함수 (페이지 리로드 없음)
      resetForNewAlbum: () => {
        set({
          albumInfo: {
            title: '',
            artist: '',
            coverImage: null,
            tags: []
          },
          tracklist: [],
          printUnits: DEFAULT_PRINT_UNITS,
          selectedPrintUnit: PrintUnitType.FRONT
        });
      },

      // 전체 초기화 함수
      resetAll: () => {
        try {
          // 로컬 스토리지 비우기
          localStorage.removeItem('md-label-maker-storage');

          // 페이지 새로고침으로 전체 앱 상태를 초기화
          // 이렇게 하면 React 훅 규칙 위반을 방지할 수 있음
          window.location.reload();
        } catch (error) {
          console.error('초기화 중 오류 발생:', error);
        }
      },

      // 로컬 스토리지 리셋 함수
      resetStorage: () => {
        try {
          // 로컬 스토리지에서 상태 제거
          localStorage.removeItem('md-label-maker-storage');
          
          // 페이지 새로고침으로 전체 앱 상태를 초기화
          // 이렇게 하면 React 훅 규칙 위반을 방지할 수 있음
          window.location.reload();
        } catch (error) {
          console.error('초기화 중 오류 발생:', error);
        }
      }
    }),
    {
      name: 'md-label-maker-storage',
      version: 1,
      migrate: (persistedState: any, version: number) => {
        // 기존 데이터가 있고 버전이 0이거나 없는 경우 (이전 버전)
        if (persistedState && persistedState.printUnits) {
          // 인쇄부 1 (FRONT)의 useText가 false인 경우 true로 변경
          if (persistedState.printUnits[PrintUnitType.FRONT]) {
            const frontUnit = persistedState.printUnits[PrintUnitType.FRONT];

            if (frontUnit.useText === false) {
              persistedState.printUnits[PrintUnitType.FRONT] = {
                ...frontUnit,
                useText: true,
                // 기본값이 없는 경우 추가
                showTitle: frontUnit.showTitle ?? true,
                showArtist: frontUnit.showArtist ?? true,
                text: frontUnit.text || { ...DEFAULT_TEXT_SETTINGS },
                titlePosition: frontUnit.titlePosition || { x: 100, y: 100 },
                artistPosition: frontUnit.artistPosition || { x: 100, y: 190 },
                titleFontSize: frontUnit.titleFontSize || 80,
                artistFontSize: frontUnit.artistFontSize || 60
              };
            }
          }

          // 인쇄부 4 (BACK)에 흰색 투명도 레이어 속성 추가
          if (persistedState.printUnits[PrintUnitType.BACK]) {
            const backUnit = persistedState.printUnits[PrintUnitType.BACK];

            // 새로운 속성들이 없는 경우 추가
            if (backUnit.showWhiteOverlay === undefined || backUnit.whiteOverlayOpacity === undefined) {
              persistedState.printUnits[PrintUnitType.BACK] = {
                ...backUnit,
                showWhiteOverlay: backUnit.showWhiteOverlay ?? false,
                whiteOverlayOpacity: backUnit.whiteOverlayOpacity ?? 0.5
              };
            }
          }

          // 모든 인쇄부의 showMdLogo를 적절히 설정
          Object.keys(persistedState.printUnits).forEach((key) => {
            const unit = persistedState.printUnits[key];
            if (unit) {
              // 인쇄부 2(SIDE), 3(BACK_TOP), 6(DISC_SIDE)는 로고 표시 안함
              const shouldShowLogo = ![PrintUnitType.SIDE, PrintUnitType.BACK_TOP, PrintUnitType.DISC_SIDE].includes(key as PrintUnitType);

              if (unit.showMdLogo !== shouldShowLogo) {
                persistedState.printUnits[key] = {
                  ...unit,
                  showMdLogo: shouldShowLogo
                };
              }

              // imageFilter 필드가 없는 경우 기본값 추가
              if (!unit.imageFilter) {
                persistedState.printUnits[key] = {
                  ...persistedState.printUnits[key],
                  imageFilter: { ...DEFAULT_IMAGE_FILTER }
                };
              }
            }
          });
        }
        
        return persistedState;
      },
      // 의도적으로 영속화할 필드만 명시 (whitelist 방식)
      // 함수/액션, clipboard(임시), undo/redo stack은 제외
      partialize: (state) => ({
        albumInfo: state.albumInfo,
        tracklist: state.tracklist,
        printUnits: state.printUnits,
        selectedPrintUnit: state.selectedPrintUnit,
        activePrintUnitOptionTab: state.activePrintUnitOptionTab,
      })
    }
  ),
  {
    limit: 50,
    // albumInfo와 tracklist 변경만 히스토리로 추적 (printUnits 이미지 데이터 포함 가능)
    // selectedPrintUnit 탭 전환, clipboard는 undo 대상에서 제외
    partialize: (state) => ({
      albumInfo: state.albumInfo,
      tracklist: state.tracklist,
      printUnits: state.printUnits,
    }),
  }
));

// 로컬 스토리지 문제 해결을 위한 수동 리셋 함수
export const resetLocalStorage = () => {
  localStorage.removeItem('md-label-maker-storage');
}